import { Link } from "react-router-dom";
import { Phone, ShieldCheck, MapPin, ArrowRight, CheckCircle2, Star } from "lucide-react";
import { useSeo, orgSchema } from "@/hooks/useSeo";
import { CallNowButton, EstimateButton, OutlineButton } from "@/components/CTAButtons";
import { CTABand } from "@/components/Layout";
import { TrustBar, ServiceCards, TestimonialGrid, ProjectGallery } from "@/components/Sections";
import LeadForm from "@/components/LeadForm";
import { business, IMG, serviceAreas, faqs } from "@/data/site";
import { track } from "@/lib/analytics";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const Home = () => {
  useSeo({
    title: `Plumber, Electrician & HVAC Contractor New York | ${business.name}`,
    description: "Licensed plumbing, electrical & HVAC contractor in New York. 24/7 emergency service, upfront pricing, free estimates. Serving all 5 boroughs. Call now!",
    jsonLd: orgSchema,
  });

  return (
    <>
      {/* HERO */}
      <section className="relative bg-[#0F2E4C] text-white overflow-hidden" data-testid="hero">
        <img src={IMG.hero} alt="New York City skyline served by Empire Home Services" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2E4C] via-[#0F2E4C]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28 lg:py-32">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-[#059669] px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="text-sm font-bold uppercase tracking-wide">{business.emergencyLine}</span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
              New York's Trusted <span className="text-[#34D399]">Plumbing, Electrical & HVAC</span> Contractor
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 mb-8 max-w-2xl">
              Fast, honest, and licensed home service across all five boroughs. Upfront pricing, clean workmanship, and a team that shows up when you need it most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <CallNowButton size="lg" label={`Call ${business.phoneDisplay}`} testid="hero-call-btn" />
              <EstimateButton size="lg" testid="hero-estimate-btn" />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
              {["Licensed & Insured", "Upfront Pricing", "Same-Day Service Available"].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#16A34A]" /> {t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24" data-testid="home-services">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">What We Do</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">Three Core Services, One Reliable Team</h2>
          <p className="text-[#475569] text-lg">Whatever your home needs, our licensed New York technicians have you covered.</p>
        </div>
        <ServiceCards />
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white border-y border-[#E2E8F0]" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Why Homeowners Choose Us</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">Local Experts You Can Actually Trust</h2>
            <div className="space-y-5">
              {[
                { t: "Licensed & Fully Insured", d: "Every job is performed by credentialed pros and backed by full insurance for your peace of mind." },
                { t: "Upfront, Honest Pricing", d: "You approve the price before we start. No surprises, no pressure, no hidden fees." },
                { t: "Fast, Reliable Response", d: "We respect your time and your home. Same-day and 24/7 emergency service available." },
                { t: `${business.yearsInBusiness}+ Years Serving New York`, d: "Thousands of satisfied neighbors across all five boroughs and beyond." },
              ].map((item) => (
                <div key={item.t} className="flex gap-4">
                  <div className="w-10 h-10 bg-[#0F2E4C] flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-white" /></div>
                  <div>
                    <h3 className="font-display font-bold text-lg mb-1">{item.t}</h3>
                    <p className="text-[#475569]">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8"><OutlineButton to="/about" testid="home-about-link">Learn More About Us <ArrowRight className="w-4 h-4" /></OutlineButton></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { n: `${business.yearsInBusiness}+`, l: "Years in Business" },
              { n: "500+", l: "5-Star Reviews" },
              { n: "24/7", l: "Emergency Service" },
              { n: "5", l: "Boroughs Served" },
            ].map((s) => (
              <div key={s.l} className="bg-[#0F2E4C] text-white p-8 text-center">
                <div className="font-display font-black text-4xl sm:text-5xl text-[#34D399] mb-2">{s.n}</div>
                <div className="text-sm uppercase tracking-wide text-slate-300">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS / BEFORE & AFTER */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24" data-testid="home-projects">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Before & After</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">See the Transformation</h2>
          <p className="text-[#475569] text-lg">Drag the slider to see real problems we've solved for New York homeowners.</p>
        </div>
        <ProjectGallery />
        <div className="text-center mt-10"><OutlineButton to="/projects" testid="home-projects-link">View All Projects <ArrowRight className="w-4 h-4" /></OutlineButton></div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-[#0F2E4C] text-white" data-testid="home-areas">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#34D399] mb-3">Where We Work</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">Proudly Serving All of New York</h2>
            <p className="text-slate-300 text-lg">From Manhattan high-rises to Staten Island homes — we're your local contractor.</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center max-w-4xl mx-auto mb-8">
            {serviceAreas.slice(0, 14).map((a) => (
              <span key={a} className="flex items-center gap-2 border border-slate-600 px-4 py-2 text-sm"><MapPin className="w-4 h-4 text-[#059669]" /> {a}</span>
            ))}
          </div>
          <div className="text-center">
            <Link to="/service-areas" className="inline-flex items-center gap-2 font-bold uppercase text-sm tracking-wide text-[#34D399] hover:text-white transition-colors" data-testid="home-areas-link">
              View All Service Areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24" data-testid="home-reviews">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex justify-center gap-1 mb-3">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-6 h-6 fill-[#EAB308] text-[#EAB308]" />)}</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">Loved by New Yorkers</h2>
          <p className="text-[#475569] text-lg">Real feedback from homeowners across the city.</p>
        </div>
        <TestimonialGrid limit={3} />
        <div className="text-center mt-10"><OutlineButton to="/reviews" testid="home-reviews-link">Read More Reviews <ArrowRight className="w-4 h-4" /></OutlineButton></div>
      </section>

      {/* ESTIMATE FORM + FAQ preview */}
      <section className="bg-white border-y border-[#E2E8F0]" data-testid="home-estimate">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Get Started</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">Request Your Free Estimate</h2>
            <p className="text-[#475569] text-lg mb-6">Tell us what you need and we'll get right back to you. No obligation, no pressure — just honest help.</p>
            <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-6 mb-6">
              <p className="font-bold mb-1">Prefer to talk now?</p>
              <a href={business.phoneHref} onClick={() => track("phone_tap", { location: "home" })} className="flex items-center gap-2 text-2xl font-display font-black text-[#059669]" data-testid="home-phone-link"><Phone className="w-6 h-6" /> {business.phoneDisplay}</a>
            </div>
            <div>
              <h3 className="font-display font-bold text-xl mb-3">Common Questions</h3>
              <Accordion type="single" collapsible className="w-full">
                {faqs.slice(0, 4).map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#E2E8F0]">
                    <AccordionTrigger className="text-left font-semibold hover:no-underline" data-testid={`home-faq-${i}`}>{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[#475569]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
          <div className="bg-[#FAFAFA] border-2 border-[#0F2E4C] p-6 sm:p-8">
            <LeadForm compact />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
};

export default Home;
